import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, message } from 'antd';
import { examQuestionService } from '../../services/examQuestion';
import { examQuestionSkillService } from '../../services/examQuestionSkill';
import { ExamQuestion } from '../../types/ExamQuestion';
import { questionSkillService } from '../../services/questionSkillService';
import { examQuestionSkillQuestionService } from '../../services/examQuestionSkillQuestionService';
import { skillService } from '../../services/skillService';
import { DeleteOutlined } from '@ant-design/icons';

interface ApiResponse {
    req: {
        page: string;
        limit: string;
        search: string;
    };
    data: ExamQuestion[];
    total: number;
    currentPage: number;
    totalPages: number;
}

const domain = process.env.REACT_APP_DOMAIN_URL

const ExamManagement: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [exams, setExams] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalCreateVisible, setIsModalCreateVisible] = useState(false);
    const [currentExam, setCurrentExam] = useState<Partial<ExamQuestion>>({});
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState<any>(null);
    const [availableQuestions, setAvailableQuestions] = useState<any[]>([]);
    const [examQuestionSkillId, setExamQuestionSkillId] = useState()
    const [listQuestion, setListQuestion] = useState<any[]>([]);
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
    const [skills, setSkills] = useState<any[]>([]);
    const [examQuestionId, setExamQuestionId] = useState<any>(null);
    const [skillId, setSkillId] = useState<any>(null);
    const [type, setType] = useState<string>('');

    const fetchExams = useCallback(async () => {
        try {
            setLoading(true);
            const response: ApiResponse = await examQuestionService.getExamQuestions({
                page: currentPage,
                pageSize: pageSize,
                type: type
            });

            setExams(response.data);
            setTotal(response.total);
        } catch (error) {
            message.error('Không thể tải danh sách câu hỏi');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, type]);

    const fetchSkills = useCallback(async () => {
        try {
            const response: ApiResponse = await skillService.getSkills({
                page: 1,
                pageSize: 100
            });
            setSkills(response.data);
        } catch (error) {
            message.error('Không thể tải danh sách kỹ năng');
        }
    }, []);

    useEffect(() => {
        fetchExams();
        fetchSkills();
    }, [fetchExams, fetchSkills]);

    const columns = [
        {
            title: 'STT',
            dataIndex: 'id',
            key: 'id',
            width: '5%',
            render: (text: string, record: ExamQuestion, index: number) => index + 1,
        },
        {
            title: 'Tên Đề Thi',
            dataIndex: 'name',
            key: 'name',
            width: '30%',
        },
        {
            title: 'Loại Đề Thi',
            dataIndex: 'type',
            key: 'type',
            width: '20%',
            render: (type: string) => {
                if (type === 'public') {
                    return <p className="text-green-500">Công khai</p>;
                } else if (type === 'private') {
                    return <p className="text-red-500">Riêng tư</p>;
                }
            }
        },
        {
            title: 'Trạng Thái',
            dataIndex: 'status',
            key: 'status',
            width: '20%',
            render: (status: string) => (
                <span className={`${status === '1' ? 'text-green-500' : 'text-red-500'}`}>
                    {status === '1' ? 'Hoạt Động' : 'Ngừng'}
                </span>
            ),
        },
        {
            title: 'Hành Động',
            key: 'actions',
            width: '30%',
            align: 'center',
            render: (text: string, record: any) => (
                <div className="flex space-x-2 items-center justify-center">
                    <Button className='bg-blue-500 text-white' onClick={() => {
                        setExamQuestionId(record.id)
                        setIsModalCreateVisible(true);
                    }}>
                        Thêm kỹ năng
                    </Button>
                    <Button className='bg-purple-500 text-white' onClick={() => handleEdit(record)}>Sửa</Button>
                    {
                        record?.type === 'public' &&
                        <Button
                            className='bg-green-500 text-white'
                            onClick={() => generaterLink(record?.ExamQuestionSkills)}>
                            Tạo link
                        </Button>
                    }
                    <Button className='bg-red-500 text-white' onClick={() => {
                        Modal.confirm({
                            title: 'Xác nhận xóa đề thi ?',
                            icon: <DeleteOutlined />,
                            okText: 'Xóa',
                            okType: 'danger',
                            cancelText: 'Hủy',
                            onOk: () => handleDelete(record.id),
                        })
                    }}>
                        Xóa
                    </Button>
                </div>
            ),
        },
    ];

    const handleEdit = (exam: ExamQuestion) => {
        setCurrentExam(exam);
        setIsModalVisible(true);
    };

    const handleDelete = async (examId: string) => {
        try {
            await examQuestionService.deleteExamQuestion(examId);
            message.success('Xóa đề thi thành công');
            fetchExams();
        } catch (error) {
            message.error('Không thể xóa đề thi');
        }
    };

    const handleAdd = async (examQuestionId, skillId) => {
        try {
            let examQuestionSkill = { examQuestionId, skillId }
            await examQuestionSkillService.createExamQuestionSkill(examQuestionSkill as Omit<any, 'id'>);
            message.success('Lưu kỹ năng đề thi thành công');
            fetchExams();
        } catch (error) {
            message.error('Không thể lưu kỹ năng đề thi');
        }
    };

    const handleDeleteExamQuestionSkill = async (examQuestionSkillId: string) => {
        try {
            await examQuestionSkillService.deleteExamQuestionSkill(examQuestionSkillId);
            message.success('Xóa kỹ năng đề thi thành công');
            fetchExams();
        } catch (error) {
            message.error('Không thể xóa kỹ năng đề thi');
        }
    };

    const handleSubmit = async () => {
        if (Object.keys(currentExam).length === 0) {
            return message.error('Thông tin đề thi không được để trống');
        }
        if (currentExam?.name === '') {
            return message.error('Tên đề thi không được để trống');
        }
        if (currentExam?.type === '') {
            return message.error('Loại đề thi không được để trống');
        }
        try {
            if (currentExam?.id) {
                await examQuestionService.updateExamQuestion(currentExam as ExamQuestion);
            } else {
                await examQuestionService.createExamQuestion(currentExam as Omit<ExamQuestion, 'id'>);
            }
            message.success('Lưu đề thi thành công');
            setIsModalVisible(false);
            fetchExams();
        } catch (error) {
            message.error('Không thể lưu đề thi');
        }
    };

    const handleTableChange = (newPagination: any) => {
        setCurrentPage(newPagination.current);
        setPageSize(newPagination.pageSize);
    };

    const generaterLink = async (examQuestionSkills: any) => {
        try {
            for (let i = 0; i < examQuestionSkills.length; i++) {
                const encodedId = btoa(examQuestionSkills[i].id);
                let link = `${domain}/practice-info/${encodedId}`;
                console.log({ link });
                examQuestionSkills[i].link = link;
                await examQuestionSkillService.updateExamQuestionSkill(examQuestionSkills[i]);
            }
            message.success('Tạo link thành công');
            fetchExams();
        } catch (error) {
            message.error('Không thể tạo link');
        }
    }

    const columnDetail = [
        {
            title: 'STT',
            dataIndex: 'id',
            key: 'id',
            render: (text: string, record: any, index: number) => index + 1,
        },
        {
            title: 'Tên Kỹ Năng',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => record?.Skill?.name,
        },
        {
            title: 'Số câu hỏi',
            dataIndex: 'numberOfQuestion',
            key: 'numberOfQuestion',
            render: (text: string, record: any) => record?.Skill?.numberOfQuestion,
        },
        {
            title: 'Thời gian làm bài',
            dataIndex: 'timeAllowe',
            key: 'timeAllowe',
            render: (text: string, record: any) => `${record?.Skill?.timeAllow} phút`,
        },
        {
            title: 'Link',
            dataIndex: 'link',
            key: 'link',
            align: 'center',
            render: (text: string, record: any) => {
                const handleCopy = () => {
                    navigator.clipboard.writeText(record?.link || '');
                    message.success('Đã sao chép link');
                };

                return record?.link && (
                    <div className="flex items-center gap-2 justify-center">
                        <p className="truncate max-w-xs">{record?.link}</p>
                        <button
                            onClick={handleCopy}
                            className="px-2 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
                        >
                            Copy
                        </button>
                    </div>
                );
            },
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (text: string, record: any) => (
                <span className={`${record?.Skill?.status === '1' ? 'text-green-500' : 'text-red-500'}`}>
                    {record?.Skill?.status === '1' ? 'Hoạt Động' : 'Ngừng'}
                </span>
            ),
        },
        {
            title: 'Hành Động',
            key: 'actions',
            dataIndex: 'actions',
            align: 'center',
            render: (text: string, record: any) => (
                <div className="flex space-x-2 items-center justify-center">
                    <Button onClick={() => handleEditDetail(record)}>Sửa</Button>
                    <Button danger onClick={() => {
                        Modal.confirm({
                            title: 'Xác nhận xóa kỹ năng đề thi ?',
                            okText: 'Xóa',
                            okType: 'danger',
                            cancelText: 'Hủy',
                            onOk: () => handleDeleteExamQuestionSkill(record?.id),
                        })
                    }}>Xóa</Button>
                </div>
            ),
        }
    ]

    const handleEditDetail = (record: any) => {
        setListQuestion(record?.ExamQuestionSkillQuestions);
        setSelectedSkill(record?.Skill?.name);
        setExamQuestionSkillId(record?.id);
        setIsDetailModalVisible(true);
    };

    const handleQuestionClick = async (skill: string, typeQuestion: string) => {
        const response = await questionSkillService.findQuestionSkillBySkillAndTypeQuestion(
            skill,
            typeQuestion
        );
        setAvailableQuestions(response?.Questions);
    }

    const handleQuestionSelect = async (examQuestionSkillId: any, questionId: any, no: number) => {
        try {
            const response = await examQuestionSkillQuestionService.findExamQuestionSkillQuestionsByExamQuestionSkillIdAndNo(
                examQuestionSkillId, no
            );

            // Tìm câu hỏi được chọn từ availableQuestions
            const selectedQuestion = availableQuestions.find(q => q.id === questionId);

            if (response) {
                // Cập nhật câu hỏi hiện có
                response.questionId = questionId;
                await examQuestionSkillQuestionService.updateExamQuestionSkillQuestion(response);

                // Cập nhật state listQuestion
                setListQuestion(prev => prev.map(q =>
                    q.id === response.id
                        ? { ...q, Question: selectedQuestion }
                        : q
                ));
            } else {
                // Tạo câu hỏi mới
                const newQuestion = await examQuestionSkillQuestionService.createExamQuestionSkillQuestion(
                    examQuestionSkillId,
                    questionId,
                    no
                );

                // Thêm câu hỏi mới vào listQuestion
                setListQuestion(prev => [...prev, {
                    ...newQuestion,
                    Question: selectedQuestion
                }]);
            }
        } catch (error) {
            message.error('Không thể cập nhật câu hỏi');
        }
    };

    const questionColumns = [
        {
            title: 'Câu hỏi số',
            dataIndex: 'questionNumber',
            key: 'questionNumber',
            width: '150px',
        },
        {
            title: 'Loại câu hỏi',
            dataIndex: 'questionType',
            key: 'questionType',
            width: '150px',
        },
        {
            title: 'Câu hỏi',
            dataIndex: 'question',
            key: 'question',
            render: (text: string, record: any) => {
                return (
                    <Select
                        style={{ width: 600 }}
                        placeholder="Chọn câu hỏi"
                        onClick={() => handleQuestionClick(selectedSkill, record?.questionType)}
                        onChange={(value) => handleQuestionSelect(examQuestionSkillId, value, record?.questionNumber)}
                        value={record?.question?.Question?.id}
                        allowClear
                    >
                        {renderQuestionOptions(record)}
                    </Select>
                )
            },
        },
    ];

    let dataSourceTypeReading = [
        { questionNumber: 1, questionType: 'Reading1', question: {} },
        { questionNumber: 2, questionType: 'Reading23', question: {} },
        { questionNumber: 3, questionType: 'Reading23', question: {} },
        { questionNumber: 4, questionType: 'Reading4', question: {} },
        { questionNumber: 5, questionType: 'Reading5', question: {} },
    ].map(item => {
        const matchingQuestion = listQuestion.find(
            q => q?.no === item.questionNumber
        );
        return {
            ...item,
            question: matchingQuestion || {}
        };
    });

    let dataSourceTypeListening = [
        { questionNumber: 1, questionType: 'Listening1To13', question: {} },
        { questionNumber: 2, questionType: 'Listening1To13', question: {} },
        { questionNumber: 3, questionType: 'Listening1To13', question: {} },
        { questionNumber: 4, questionType: 'Listening1To13', question: {} },
        { questionNumber: 5, questionType: 'Listening1To13', question: {} },
        { questionNumber: 6, questionType: 'Listening1To13', question: {} },
        { questionNumber: 7, questionType: 'Listening1To13', question: {} },
        { questionNumber: 8, questionType: 'Listening1To13', question: {} },
        { questionNumber: 9, questionType: 'Listening1To13', question: {} },
        { questionNumber: 10, questionType: 'Listening1To13', question: {} },
        { questionNumber: 11, questionType: 'Listening1To13', question: {} },
        { questionNumber: 12, questionType: 'Listening1To13', question: {} },
        { questionNumber: 13, questionType: 'Listening1To13', question: {} },
        { questionNumber: 14, questionType: 'Listening14', question: {} },
        { questionNumber: 15, questionType: 'Listening15', question: {} },
        { questionNumber: 16, questionType: 'Listening1617', question: {} },
        { questionNumber: 17, questionType: 'Listening1617', question: {} },
    ].map(item => {
        const matchingQuestion = listQuestion.find(
            q => q?.no === item.questionNumber
        );
        return {
            ...item,
            question: matchingQuestion || {}
        };
    });

    const renderQuestionOptions = (record: any) => {
        let allQuestions = [...(availableQuestions || [])];

        // Thêm câu hỏi hiện tại vào danh sách nếu có
        if (record?.question?.Question) {
            const currentQuestion = record.question.Question;
            allQuestions = [
                ...allQuestions.filter(q => q.id !== currentQuestion.id),
                currentQuestion
            ];
        }

        if (allQuestions.length > 0) {
            return allQuestions.map(q => (
                <Select.Option key={q.id} value={q.id}>
                    {q.content || `Câu hỏi ${q.id}`}
                </Select.Option>
            ));
        }

        return (
            <Select.Option key={0} value={null}>
                Không có câu hỏi
            </Select.Option>
        );
    };

    return (
        <div className="p-6">
            <div className="flex justify-between mb-4">
                <h1 className="text-2xl font-bold">Quản Lý Đề Thi</h1>
                <div>
                    <Select
                        style={{ width: 200, marginRight: 8 }}
                        value={type}
                        onChange={(e) => {
                            setType(e);
                            setCurrentPage(1);
                        }}
                        placeholder="Loại đề thi"
                    >
                        <Select.Option value="">Tất cả</Select.Option>
                        <Select.Option value="public">Công khai</Select.Option>
                        <Select.Option value="private">Riêng tư</Select.Option>
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => {
                            setCurrentExam({});
                            setIsModalVisible(true);
                        }}
                    >
                        Thêm Đề Thi
                    </Button>
                </div>
            </div>
            <Table
                tableLayout='fixed'
                columns={columns as any}
                bordered
                dataSource={exams}
                rowKey="id"
                scroll={{ y: 600 }}
                expandable={{
                    expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
                    onExpand: (expanded, record) => {
                        setExpandedRowKey(expanded ? record.id : null);
                    },
                    expandedRowRender: (record) => {
                        return (
                            <div className="px-4">
                                {record?.ExamQuestionSkills?.length > 0 ? (
                                    <Table
                                        dataSource={record?.ExamQuestionSkills}
                                        pagination={false}
                                        bordered
                                        size="small"
                                        columns={columnDetail as any}
                                    />
                                ) : (
                                    <p className="text-gray-500">Không có kỹ năng nào</p>
                                )}
                            </div>
                        )
                    },
                }}
                pagination={{
                    size: 'small',
                    current: currentPage,
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: [5, 10, 20],
                    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
                }}
                loading={loading}
                onChange={handleTableChange}
            />

            <Modal
                title={currentExam.id ? 'Sửa Đề Thi' : 'Thêm Đề Thi'}
                visible={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Tên Đề Thi">
                        <Input
                            value={currentExam.name}
                            onChange={(e) => setCurrentExam({ ...currentExam, name: e.target.value })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Chỉnh sửa câu hỏi"
                visible={isDetailModalVisible}
                onOk={() => {
                    setIsDetailModalVisible(false);
                }}
                onCancel={() => setIsDetailModalVisible(false)}
                width={1000}
                style={{ top: 50 }}
                bodyStyle={{
                    maxHeight: 'calc(100vh - 200px)',
                    overflowY: 'auto'
                }}
            >
                <Table
                    columns={questionColumns}
                    dataSource={
                        selectedSkill === 'Reading' ? dataSourceTypeReading : dataSourceTypeListening
                    }
                    pagination={false}
                    bordered
                    scroll={{ y: 'calc(100vh - 300px)' }}
                />
            </Modal>

            <Modal
                title={currentExam.id ? 'Sửa Đề Thi' : 'Thêm Đề Thi'}
                visible={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Tên Đề Thi">
                        <Input
                            value={currentExam.name}
                            onChange={(e) => setCurrentExam({ ...currentExam, name: e.target.value })}
                        />
                    </Form.Item>
                    <Form.Item label="Loại Đề Thi">
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Chọn loại đề thi"
                            onChange={(e) => setCurrentExam({ ...currentExam, type: e })}
                            value={currentExam.type}
                        >
                            <Select.Option value="public">Công khai</Select.Option>
                            <Select.Option value="private">Riêng tư</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Thêm kỹ năng"
                visible={isModalCreateVisible}
                onOk={() => {
                    handleAdd(examQuestionId, skillId);
                    setIsModalCreateVisible(false);
                }}
                onCancel={() => setIsModalCreateVisible(false)}
                width={500}
                style={{ top: 50 }}
            >
                {
                    skills.length > 0 && (
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Chọn kỹ năng"
                            onChange={(e) => {
                                setSkillId(e);
                            }}
                        >
                            {
                                skills.map(s => (
                                    <Select.Option key={s.id} value={s.id}>
                                        {s.name}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    )
                }
            </Modal>
        </div>
    );
};

export default ExamManagement;